export const DEVTOOLS_DOCK_SAMPLE_INTERVAL = 250;
export const DEVTOOLS_DOCK_REQUIRED_SAMPLES = 3;
export const DEVTOOLS_DOCK_MIN_VIEWPORT_LOSS_RATIO = 0.24;
export const DEVTOOLS_DOCK_MIN_HORIZONTAL_GAP = 360;
export const DEVTOOLS_DOCK_MIN_VERTICAL_GAP = 280;

// disable-devtool's Size detector multiplies inner dimensions by devicePixelRatio, which turns
// ordinary zoom levels into large false gaps. These thresholds use raw CSS pixels and the monitor
// below requires the signal to remain present across several samples before confirming it.

const readFiniteDimension = value => {
  const dimension = Number(value);
  return Number.isFinite(dimension) ? dimension : 0;
};

export const getListingViewportGaps = browserWindow => {
  const outerWidth = readFiniteDimension(browserWindow?.outerWidth);
  const innerWidth = readFiniteDimension(browserWindow?.innerWidth);
  const outerHeight = readFiniteDimension(browserWindow?.outerHeight);
  const innerHeight = readFiniteDimension(browserWindow?.innerHeight);

  return {
    outerWidth,
    innerWidth,
    outerHeight,
    innerHeight,
    horizontalGap: Math.max(0, outerWidth - innerWidth),
    verticalGap: Math.max(0, outerHeight - innerHeight),
  };
};

export const isLikelyDockedDevtool = browserWindow => {
  const {
    outerWidth,
    innerWidth,
    outerHeight,
    innerHeight,
    horizontalGap,
    verticalGap,
  } = getListingViewportGaps(browserWindow);

  if (outerWidth <= 0 || innerWidth <= 0 || outerHeight <= 0 || innerHeight <= 0) {
    return false;
  }

  const horizontalThreshold = Math.max(
    DEVTOOLS_DOCK_MIN_HORIZONTAL_GAP,
    outerWidth * DEVTOOLS_DOCK_MIN_VIEWPORT_LOSS_RATIO
  );
  const verticalThreshold = Math.max(
    DEVTOOLS_DOCK_MIN_VERTICAL_GAP,
    outerHeight * DEVTOOLS_DOCK_MIN_VIEWPORT_LOSS_RATIO
  );

  return horizontalGap >= horizontalThreshold || verticalGap >= verticalThreshold;
};

export const startListingDevtoolDockMonitor = ({
  browserWindow = typeof window === 'undefined' ? undefined : window,
  pageDocument = typeof document === 'undefined' ? undefined : document,
  onConfirmed,
  sampleInterval = DEVTOOLS_DOCK_SAMPLE_INTERVAL,
  requiredSamples = DEVTOOLS_DOCK_REQUIRED_SAMPLES,
} = {}) => {
  if (
    !browserWindow ||
    typeof browserWindow.setInterval !== 'function' ||
    typeof browserWindow.clearInterval !== 'function' ||
    typeof onConfirmed !== 'function'
  ) {
    return () => {};
  }

  let consecutiveSuspiciousSamples = 0;
  let intervalId;
  let stopped = false;

  const stop = () => {
    if (stopped) {
      return;
    }

    stopped = true;
    if (intervalId !== undefined) {
      browserWindow.clearInterval(intervalId);
    }
  };

  const sample = () => {
    if (stopped) {
      return;
    }

    const pageIsHidden =
      pageDocument?.hidden === true || pageDocument?.visibilityState === 'hidden';
    if (pageIsHidden || !isLikelyDockedDevtool(browserWindow)) {
      consecutiveSuspiciousSamples = 0;
      return;
    }

    consecutiveSuspiciousSamples += 1;
    if (consecutiveSuspiciousSamples >= requiredSamples) {
      stop();
      onConfirmed();
    }
  };

  intervalId = browserWindow.setInterval(sample, sampleInterval);
  sample();

  return stop;
};
